/**
 * Upgrade Prompt Component
 * 
 * Shows when users need to upgrade to access premium features.
 */

import React from 'react';
import { Crown, Lock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../context/subscription/SubscriptionContext';

interface UpgradePromptProps {
  feature?: string;
  requiredPlan?: string;
  title?: string;
  description?: string;
  benefits?: string[];
  className?: string;
  inline?: boolean;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  feature,
  requiredPlan,
  title,
  description,
  benefits = [],
  className = '',
  inline = false
}) => {
  const navigate = useNavigate();
  const { currentSubscription } = useSubscription();

  const defaultTitle = feature 
    ? `Unlock ${feature}` 
    : 'Upgrade Your Plan';

  const defaultDescription = requiredPlan
    ? `This feature requires a ${requiredPlan} plan or higher.`
    : 'Upgrade to access premium features and increased limits.';

  const defaultBenefits = benefits.length > 0 ? benefits : [
    'Increased simulation limits',
    'Access to advanced use cases',
    'Priority customer support',
    'Advanced analytics and insights'
  ];

  const handleUpgrade = () => {
    navigate('/subscription/pricing');
  };

  if (inline) {
    return (
      <div className={`bg-gradient-to-r from-primary/10 to-blue-50 border border-primary/20 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center mr-3">
              <Crown className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">
                {title || defaultTitle}
              </h4>
              <p className="text-sm text-gray-600">
                {description || defaultDescription}
              </p>
            </div>
          </div>
          <button
            onClick={handleUpgrade}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            Upgrade
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-md mx-auto bg-white rounded-lg shadow-lg border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Crown className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {title || defaultTitle}
        </h3>
        <p className="text-gray-600">
          {description || defaultDescription}
        </p>
      </div>

      {/* Current Plan Info */}
      {currentSubscription && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Current Plan</span>
            <span className="text-sm text-gray-600">{currentSubscription.name}</span>
          </div>
        </div>
      )}

      {/* Benefits */}
      <div className="space-y-3 mb-6">
        <h4 className="font-medium text-gray-900">What you'll get:</h4>
        {defaultBenefits.map((benefit, index) => (
          <div key={index} className="flex items-center">
            <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-sm text-gray-700">{benefit}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={handleUpgrade}
          className="w-full flex items-center justify-center px-4 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors font-medium"
        >
          View Pricing Plans
          <ArrowRight className="w-4 h-4 ml-2" />
        </button>
        
        <button
          onClick={() => window.history.back()}
          className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

/**
 * Locked Feature Component
 * 
 * Shows a locked state for premium features.
 */
export const LockedFeature: React.FC<{
  children: React.ReactNode;
  feature?: string;
  requiredPlan?: string;
  className?: string;
}> = ({ children, className = '' }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate('/subscription/pricing');
  };

  return (
    <div className={`relative group ${className}`} onClick={handleClick}>
      {/* Content with overlay */}
      <div className="relative">
        <div className="opacity-90">
          {children}
        </div>
        
        {/* Lock icon in top right */}
        <div className="absolute top-2 right-2 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center shadow-sm">
          <Lock className="w-4 h-4 text-gray-400" />
        </div>

        {/* Hover tooltip */}
        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
          Subscribe to get access
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;